import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

function ChatMessage({ message }) {
  const isBot = message.sender === 'bot';
  const isSystem = message.sender === 'system';

  // System messages (persona changes)
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-xs font-medium border">
          <span className="mr-2">🔄</span>
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
      <div className={`flex gap-3 max-w-[80%] ${isBot ? 'flex-row' : 'flex-row-reverse'}`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${
          isBot ? 'bg-purple-600' : 'bg-blue-600'
        }`}>
          {isBot ? 'AI' : 'U'}
        </div>

        {/* Message Content */}
        <div className="flex flex-col gap-1">
          <div className={`px-4 py-3 rounded-2xl ${
            isBot 
              ? 'bg-white text-gray-800 shadow-sm border border-gray-200' 
              : 'bg-blue-600 text-white'
          }`}>
            {isBot ? (
              <div className="prose prose-sm max-w-none prose-pre:bg-gray-900 prose-pre:text-gray-100">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    // Custom rendering for <thinking> tags
                    p: ({node, children, ...props}) => {
                      const text = String(children);
                      if (text.includes('<thinking>') || text.includes('</thinking>')) {
                        return (
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 my-2">
                            <div className="text-yellow-800 text-xs font-semibold mb-1">💭 Thinking Process</div>
                            <p className="text-yellow-700 text-sm" {...props}>
                              {text.replace(/<\/?thinking>/g, '')}
                            </p>
                          </div>
                        );
                      }
                      return <p {...props}>{children}</p>;
                    },
                    // Style code blocks
                    code: ({node, inline, className, children, ...props}) => {
                      const match = /language-(\w+)/.exec(className || '');
                      return inline ? (
                        <code className="bg-gray-100 text-pink-600 px-1 py-0.5 rounded text-sm" {...props}>
                          {children}
                        </code>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {message.text}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                {message.text}
              </p>
            )}
          </div>
          <span className={`text-xs text-gray-400 px-2 ${isBot ? 'text-left' : 'text-right'}`}>
            {message.timestamp}
          </span>
        </div>
      </div>
    </div>
  );
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    sender: PropTypes.oneOf(['user', 'bot', 'system']).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired
  }).isRequired
};

export default ChatMessage;
